import React from 'react';

import { FormattedMessage } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { FlagsProvider } from '@atlaskit/flag';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { messages } from './messages';

const titleWrapperStyles = xcss({
	minWidth: '270px',
	paddingBlock: 'space.100',
});

const closeIconWrapperStyles = xcss({
	marginLeft: 'auto',
});

const containerStyles = xcss({
	height: '100%',
});

type ExtensionsConfigWrapperProps = React.PropsWithChildren<{
	buttonIcon?: React.ReactNode;
	closeConfigPanel: () => void;
	footerComponent?: React.FC;
	title: React.ReactNode;
}>;

export const ExtensionsConfigWrapper = ({
	title,
	children,
	footerComponent: FooterComponent,
	buttonIcon = null,
	closeConfigPanel,
}: ExtensionsConfigWrapperProps): React.JSX.Element => {
	// Opt out of React Compiler: when `pcs-3808077-links-menu-react-compiler-fixes` is off,
	// `FooterComponent` is invoked as a plain function (`FooterComponent({})`), which auto-memoization
	// turns into a hook-count mismatch (React #300). See HOT-304566 / PIR-302437.
	// Remove once that fix flag is fully rolled out.
	'use no memo';

	return (
		<FlagsProvider>
			<Flex
				columnGap="space.300"
				justifyContent="space-between"
				direction="column"
				xcss={containerStyles}
			>
				<Stack space="space.300">
					<Flex
						justifyContent="start"
						alignItems="center"
						gap="space.100"
						xcss={titleWrapperStyles}
					>
						{buttonIcon}
						<Text size="large" weight="medium">
							{title}
						</Text>
						<Box xcss={closeIconWrapperStyles}>
							<Tooltip hideTooltipOnClick content={<FormattedMessage {...messages.close} />}>
								<IconButton
									onClick={closeConfigPanel}
									appearance="subtle"
									icon={CrossIcon}
									label={<FormattedMessage {...messages.close} />}
									testId="panel-close-button"
									isTooltipDisabled={false}
								/>
							</Tooltip>
						</Box>
					</Flex>
					{children}
				</Stack>
				{FooterComponent && <FooterComponent />}
			</Flex>
		</FlagsProvider>
	);
};
